import {
  CANVAS_ELEMENT_TYPES,
  LABELABLE_ELEMENT_TYPES,
} from "../nativeCanvasElements";
import { getElementFrame } from "./elementBounds";

const DEFAULT_FONT_SIZE = 20;
const TEXT_LINE_HEIGHT_FACTOR = 1.3;
const MIN_CONTENT_WIDTH = 8;
const MAX_SHAPE_HEIGHT = 4000;
const NARROW_CHARACTER_PATTERN = /[iIl1.,'`!|:;]/;
const WIDE_CHARACTER_PATTERN = /[MW@#%&]/;
const MAX_SINGLE_BYTE_CODE_POINT = 0xff;

const CONTENT_WIDTH_RATIOS = Object.freeze({
  [CANVAS_ELEMENT_TYPES.DIAMOND]: 0.6,
  [CANVAS_ELEMENT_TYPES.ELLIPSE]: 0.72,
  [CANVAS_ELEMENT_TYPES.RECTANGLE]: 0.86,
});

const VERTICAL_PADDING_FACTORS = Object.freeze({
  [CANVAS_ELEMENT_TYPES.DIAMOND]: 1.1,
  [CANVAS_ELEMENT_TYPES.ELLIPSE]: 1,
  [CANVAS_ELEMENT_TYPES.RECTANGLE]: 0.6,
});

const getFontSize = (element) => {
  const fontSize = Number(element.style?.fontSize ?? element.fontSize);
  return Number.isFinite(fontSize) && fontSize > 0 ? fontSize : DEFAULT_FONT_SIZE;
};

const getCharacterWidthFactor = (character) => {
  if (character.codePointAt(0) > MAX_SINGLE_BYTE_CODE_POINT) return 1;
  if (NARROW_CHARACTER_PATTERN.test(character)) return 0.35;
  if (WIDE_CHARACTER_PATTERN.test(character)) return 0.9;
  if (character === " ") return 0.38;
  if (character === character.toUpperCase() && /[A-Z]/.test(character)) {
    return 0.72;
  }
  return 0.62;
};

const estimateTextWidth = ({ fontSize, text }) =>
  Array.from(text).reduce(
    (width, character) => width + getCharacterWidthFactor(character) * fontSize,
    0
  );

const splitLongToken = ({ maxWidth, measureText, token }) => {
  const chunks = [];
  let currentChunk = "";

  Array.from(token).forEach((character) => {
    const candidate = `${currentChunk}${character}`;
    if (currentChunk && measureText(candidate) > maxWidth) {
      chunks.push(currentChunk);
      currentChunk = character;
      return;
    }
    currentChunk = candidate;
  });
  if (currentChunk) chunks.push(currentChunk);
  return chunks;
};

const appendChunk = ({ chunk, lines, maxWidth, measureText }) => {
  const currentLine = lines[lines.length - 1] ?? "";
  const candidate = currentLine ? `${currentLine} ${chunk}` : chunk;
  if (!currentLine || measureText(candidate) <= maxWidth) {
    lines[lines.length - 1] = candidate;
    return;
  }
  lines.push(chunk);
};

const wrapParagraph = ({ maxWidth, measureText, paragraph }) => {
  if (!paragraph.trim()) return [""];
  const lines = [""];
  paragraph.trim().split(/\s+/).forEach((token) => {
    const chunks = splitLongToken({ maxWidth, measureText, token });
    chunks.forEach((chunk) =>
      appendChunk({ chunk, lines, maxWidth, measureText })
    );
  });
  return lines;
};

const getContentMetrics = ({ element, fontSize, frame }) => {
  const widthRatio = CONTENT_WIDTH_RATIOS[element.type] ?? 0.86;
  const verticalFactor = VERTICAL_PADDING_FACTORS[element.type] ?? 0.6;
  const contentWidth = Math.max(
    frame.width * widthRatio - fontSize * 0.4,
    MIN_CONTENT_WIDTH
  );

  return {
    contentWidth,
    horizontalPadding: Math.max((frame.width - contentWidth) / 2, 0),
    verticalPadding: Math.max(fontSize * verticalFactor, 12),
  };
};

/**
 * Wraps a shape label within its safe inner width and reports the height it needs.
 * @param {{ element: object, label?: string, measureText?: (text: string) => number }} params
 * @returns {{ contentWidth: number, horizontalPadding: number, lineHeight: number, lines: Array<string>, requiredHeight: number }}
 */
export const getShapeLabelLayout = ({ element, label, measureText }) => {
  const fontSize = getFontSize(element);
  const frame = getElementFrame(element);
  const metrics = getContentMetrics({ element, fontSize, frame });
  const measure = measureText ?? ((text) => estimateTextWidth({ fontSize, text }));
  const value = String(label ?? element.label ?? "");
  const lines = value
    ? value.split("\n").flatMap((paragraph) =>
        wrapParagraph({
          maxWidth: metrics.contentWidth,
          measureText: measure,
          paragraph,
        })
      )
    : [];
  const lineHeight = fontSize * TEXT_LINE_HEIGHT_FACTOR;
  const requiredHeight = Math.min(
    Math.ceil(lines.length * lineHeight + metrics.verticalPadding * 2),
    MAX_SHAPE_HEIGHT
  );

  return { ...metrics, lineHeight, lines, requiredHeight };
};

/**
 * Grows labelable shapes vertically when wrapped text needs more room.
 * @param {object} element
 * @returns {object}
 */
export const normalizeShapeLabelLayout = (element) => {
  if (!LABELABLE_ELEMENT_TYPES.includes(element?.type) || !element.label) {
    return element;
  }
  const frame = getElementFrame(element);
  const { requiredHeight } = getShapeLabelLayout({ element });
  const height = Math.max(frame.height, requiredHeight);
  return height === frame.height ? element : { ...element, height };
};

/**
 * @param {Array<object>} elements
 * @returns {Array<object>}
 */
export const normalizeShapeLabelLayouts = (elements) =>
  elements.map(normalizeShapeLabelLayout);
